import React, {  } from 'react';
import { useSelector, useDispatch } from "react-redux";
//internal and external components import
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import { TableRow, Tooltip, Typography } from '@mui/material/';
import { sortData } from '../../../common/SortSupport';
import { EnhancedTableHead } from '../../EnhancedTableHead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faLock, faStar, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { Box } from '@mui/material';
import CommentsDocsActionsIconWrapper from '../../CommentsDocsActionsIconWrapper';
import { colors } from '../../../common/pallette/colors';
import { getCriteriaGroups, getEvaluationCriteriaStandards, getSelfAssessmentCriteria, restrictCriteriaUsers, restrictStandardUsers, saveStandardsSelfAssessmentComment } from '../../../api/evaluation';
import TableSkeleton from '../../TableSkeleton';
import { resetSaActions, resetSaComments, resetSaEvidences, resetSelectedSaStandard, resetStandardStatuses, setCriteriaGroups, setSaCriteria, setSaStandards, setSelectedSaCriteria, setSelectedSaStandard } from '../../../redux/reducers/evaluation';
import { evaluationSelectedStandardsActionsRegex, evaluationSelectedStandardsRegex, evaluationSelectedStandardsReportRegex, genUID } from '../../../common/StaticExports';
import SAStandardsDetail from '../../../containers/StandardsDetail/SAStandardsDetail';
import { selfAssessmentStatusDisplayName } from '../../../api/types';
import { t } from 'i18next';
import AccessManager from '../../../containers/AccessManager';
import { setSnackBarData } from '../../../redux/reducers/common';
import { getAccessCount } from '../../../common/logics';
import { useLocation, useNavigate } from 'react-router';
import { select } from 'd3-selection';

const StandardsTable = () => {
  const currentCriteria = useSelector((state:any) => state.evaluation.selectedSaCriteria);
  const currentClub = useSelector((state:any) => state.clubDetails);
  const standards = useSelector((state:any) => state.evaluation.saStandards);
  const selfAssessmentComment = useSelector((state:any) => state.evaluation.saComments);
  const standardStatuses = useSelector((state:any) => state.evaluation.standardStatuses);
  const [saStandards, saStandardsSet] = React.useState(standards);
  const criterias = useSelector((state: any) => state.evaluation.saCriteria);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  //supporting contents
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('uid');
  const detail = useSelector((state:any) => state.evaluation.selectedSaStandard);
  // const [isOpen, setIsOpen] = React.useState(999);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState(false);
  const initialAccessState = {
    isOpen: false,
    itemId: null,
  }
  const [manageAccess, setManageAccess] = React.useState(initialAccessState);
  const userId = useSelector((state:any) => state.common.user)?.id;

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  const fetchCriteriaStandards = async () => {
    const response = await getEvaluationCriteriaStandards({clubId: currentClub.id, criteriaId: currentCriteria.id});
    dispatch(setSaStandards(response));
    saStandardsSet(response);
    return response;
  }

  React.useEffect(() => {
    if (currentCriteria?.id && currentClub?.id && !standards && !isFetching) {
      setIsFetching(true);
      fetchCriteriaStandards().then((response:any) => {
        setIsFetching(false);
      });
    }
    if (
      evaluationSelectedStandardsRegex.test(location.pathname) ||
      evaluationSelectedStandardsActionsRegex.test(location.pathname)
    ) {
      const tempsplitArr = location.pathname?.split("/");
      const tempStdId = tempsplitArr[5];
      const tempCriteriaId = tempsplitArr[1];
      // navigate(`/evaluation/criteria/${currentCriteria?.id}/standards/${tempStdId}`);
      if (!detail) {
        const tempCriteriaDetails: any = {
          ...detail,
          id: tempCriteriaId,
        };
        dispatch(setSelectedSaCriteria(tempCriteriaDetails));
      }
      if (!detail) {
        const tempStdDetails: any = {
          ...detail,
          id: tempStdId,
        };
        dispatch(setSelectedSaStandard(tempStdDetails));
      }
      setIsOpen(true);
    }
  }, [currentClub, currentCriteria, standardStatuses, criterias])
  
  let visibleRows = React.useMemo(
    () =>
      saStandards && saStandards.length > 0 &&
      sortData(saStandards, orderBy, order),
    [order, orderBy, saStandards],
  );

  const head = [
    {
      key: 'uid', displayName: 'id',
    },
    {
      key: 'name', displayName: 'standards',
    },
    {
      key: '', displayName: 'star',
    },
    {
      key: '', displayName: '',
    },
    {
      key: 'status', displayName: 'progress',
    },
    {
      key: '', displayName: '',
    },
  ]

  const manageClose = (isUpdate: any) => {
    if (!evaluationSelectedStandardsActionsRegex.test(location.pathname)) {
      setIsOpen(false);
      navigate(`/evaluation/criteria/${currentCriteria?.id}/standards`);
      dispatch(resetSelectedSaStandard());
      dispatch(resetSaActions());
      dispatch(resetSaComments());
      dispatch(resetSaEvidences());
      dispatch(resetStandardStatuses());
      if(isUpdate) {
        saveStandardsSelfAssessmentComment({
          standardId: detail?.id, 
          clubId: currentClub.id, 
          comment: selfAssessmentComment?.comment});
      }
    }
  };
  //fetch SA Criteria
  const fetchCriteria = async () => {
    const response = await getSelfAssessmentCriteria({ clubId: currentClub?.id });
    const groups = await getCriteriaGroups({ clubId: currentClub?.id });
    dispatch(setSaCriteria(response));
    dispatch(setCriteriaGroups(groups));
  };

  const triggerStandardsUpdate = (restrictedUsers:any) => {
          restrictStandardUsers({clubId: currentClub?.id, standardId: manageAccess?.itemId, users: restrictedUsers})
          .then(() => {
            const warningData: any = {
              isOpen: true,
              severity: "success",
              message: t("ACCESS_UPDATED_SUCCESSFULLY"),
            };
            dispatch(setSnackBarData(warningData));
            fetchCriteriaStandards();
          })
          .catch((error:any) => {
            const warningData: any = {
              isOpen: true,
              severity: "error",
              message: error?.message || t("ACCESS_UPDATE_FAILED"),
            };
            dispatch(setSnackBarData(warningData));
            saStandardsSet(standards);
          });
  }

  const manageUserAccess = (isUpdated: any, restrictedUsers:any, newUnRestrictedUsers?: any, unResestrictedUsersForAllStandards?: any) => {
      if(isUpdated) {
        if (newUnRestrictedUsers !== null && newUnRestrictedUsers !== undefined && newUnRestrictedUsers.length > 0) {
          var criteriaRestrictedUserIds = currentCriteria?.restrictions?.filter((res:any) => res.isRestricted)?.map((x:any) => x.userId) || [];
          var updatedCriteriaRestrictedUsers = newUnRestrictedUsers ? criteriaRestrictedUserIds?.filter((num:any) => !newUnRestrictedUsers?.includes(num)) : [];
          var tempUpdatedSelectedCriteria = {...currentCriteria, restrictions: currentCriteria?.restrictions?.map((res:any) => {
            if(updatedCriteriaRestrictedUsers?.includes(res.userId)) {
              return {...res, isRestricted: true}
            } return {...res, isRestricted: false};
          })};
          dispatch(setSelectedSaCriteria(tempUpdatedSelectedCriteria));
          restrictCriteriaUsers({clubId: currentClub?.id, criteriaId: currentCriteria?.id, users: updatedCriteriaRestrictedUsers})
          .then(() => {
            //updating standards table after criteria restriction update
            var updatedStandard = saStandards?.find((x:any) => x.id == manageAccess?.itemId);
            updatedStandard = {...updatedStandard, restrictions: updatedStandard?.restrictions?.map((res:any) => {
              if(restrictedUsers?.includes(res.userId)) {
                return {...res, isRestricted: true}
              } return res;
            })};
            //manage other standards restrictions as it is / as required
            var otherStandards = standards?.filter((c:any) => c.id !== updatedStandard.id);
            otherStandards?.map((os:any) => {
              var tempUpdatedRestrictedUsers = os?.restrictions?.filter((res:any) => res.isRestricted)?.map((x:any) => x.userId) || [];
              tempUpdatedRestrictedUsers = [...tempUpdatedRestrictedUsers, ...newUnRestrictedUsers];
              if(unResestrictedUsersForAllStandards && unResestrictedUsersForAllStandards.length > 0) {
                tempUpdatedRestrictedUsers = tempUpdatedRestrictedUsers?.filter((num:any) => !unResestrictedUsersForAllStandards?.includes(num));
              }
              restrictStandardUsers({clubId: currentClub?.id, standardId: os?.id, users: tempUpdatedRestrictedUsers});
            });
            //manage current standard restriction update in the table
            saStandardsSet(saStandards?.map((c:any) => c.id == updatedStandard.id ? updatedStandard : c));
            triggerStandardsUpdate(restrictedUsers);
            fetchCriteria();
          })
          .catch((error:any) => {
            const warningData: any = {
              isOpen: true,
              severity: "error",
              message: error?.message || t("ACCESS_UPDATE_FAILED"),
            };
            dispatch(setSnackBarData(warningData));
          });
        } else {
          triggerStandardsUpdate(restrictedUsers);
        }
      }
      setManageAccess(initialAccessState);
  }

  //table contents
    return(
        <React.Fragment>
            <Box sx={{ position: 'relative' }}>
              <SAStandardsDetail isOpen={isOpen} detail={detail} handleClose={manageClose}/>
              <AccessManager isOpen={manageAccess?.isOpen} handleIsOpen={manageUserAccess} from={'evaluationStandards'} itemId={manageAccess?.itemId} criteriaRestrictions={currentCriteria?.restrictions} />
              <TableContainer sx={{ overflowY: 'auto' }}>
                <Table stickyHeader size="small" aria-label="a dense table">
                    <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    tableHeads={head}
                    />
                      <TableBody>
                        {
                          !isFetching &&
                          visibleRows &&
                          visibleRows?.length ?
                          visibleRows?.map((d: any, i: number) => {
                            const completed = selfAssessmentStatusDisplayName[d?.status] == 'READY' ||
                                  selfAssessmentStatusDisplayName[d?.status] == 'COMPLETE';
                            const totalUsers = d?.restrictions?.length || 0;
                            const accessCount = getAccessCount(d?.restrictions);
                            const isPrivate = totalUsers == accessCount ? false : true;
                            const restrictedUsers = d?.restrictions?.filter((res:any) => res.isRestricted)?.map((x:any) => x.userId) || [];
                            const isRestricted = restrictedUsers?.includes(userId);
                            return (
                            <>
                              <TableRow
                              className={isRestricted ? 'selectedRow cursorNotAllowed' : 'hoverBG'}
                              sx={{ cursor: 'pointer' }}
                              key={`standards-table-row-${i}`}
                              >
                              <TableCell className='fs12' onClick={() => {
                                }}>{genUID(d.uid)}</TableCell>
                              <TableCell className='fs12'
                              onClick={() => {
                                !isRestricted &&
                                navigate(
                                  `/evaluation/criteria/${currentCriteria?.id}/standards/${d?.id}`
                                );
                                !isRestricted && dispatch(setSelectedSaStandard(d));
                                !isRestricted && setIsOpen(true);
                              }} 
                              >{d.name}</TableCell>
                              <TableCell className='fs12'>
                                  {
                                      Array.from({length: d.starRating}, () => <FontAwesomeIcon icon={faStar} style={{color: colors.yellow.bright,}} />)
                                  }
                              </TableCell>
                              {/* <TableCell className='fs12'> */}
                                  {/* <SelectBoxPrimary 
                                  label={''} 
                                  report={'siteReview'} 
                                  options={[0, 1, 2, 3]} 
                                  isStatus={true} 
                                  manageChange={(e:any) => {updateState({...d, status: e})}}
                                  selected={d.status} /> */}
                              {/* </TableCell> */}
                              <TableCell className='fs12'><CommentsDocsActionsIconWrapper actions={d.noOfActions} comments={d.noOfComments} docs={d.noOfEvidences} /></TableCell>
                              <TableCell className='fs12'>
                                {t(selfAssessmentStatusDisplayName[d?.status])}
                                {completed && <FontAwesomeIcon icon={faCircleCheck} className='ml10' color={colors.green.bright} />}
                              </TableCell>
                              <TableCell>
                                <Tooltip title={!isRestricted && t('TDM_MANAGE_ACCESS')} arrow placement="top"  className={isPrivate ? 'closedLock' : ''}>
                                <span>
                                    <Typography className='fs12 lockCountLabel mr5'>{isPrivate ? accessCount : ''}</Typography>
                                    <FontAwesomeIcon className={isPrivate ? 'icon closedLock' : 'icon'} icon={isPrivate ? faLock : faUnlock} onClick={(e:any) => {
                                        e.stopPropagation();
                                        !isRestricted && setManageAccess({isOpen: true, itemId: d.id});
                                        }} />
                                </span>
                                </Tooltip>
                              </TableCell>
                              </TableRow>
                            </> 
                            )}):
                            !isFetching && visibleRows?.length === 0 ?
                              <TableRow>
                                <TableCell colSpan={head?.length} align="center" className='fs14 p20'>
                                  {t('NO_STANDARDS_FOUND')}
                                </TableCell>
                              </TableRow> :
                            isFetching &&
                              <TableSkeleton rowCount={8} cellCount={head?.length} />
                        }
                      </TableBody>
                </Table>
              </TableContainer>
            </Box>
        </React.Fragment>
    )
}

export default StandardsTable;